import { useEffect, useMemo, useState } from "react";
import {
  Ban,
  Copy,
  Download,
  RefreshCw,
  Search,
  Trash2,
  Check,
  Clock3,
  Infinity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface CdkCode {
  id: string;
  code: string;
  description: string | null;
  is_used: boolean;
  used_by: string | null;
  used_at: string | null;
  expires_at: string | null;
  grant_premium_hours: number | null;
  premium_until: string | null;
  created_at: string;
}

function generateRandomCode(length = 16): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let result = "";
  for (let i = 0; i < length; i += 1) {
    if (i > 0 && i % 4 === 0) result += "-";
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function downloadCsv(filename: string, headers: string[], rows: string[][]): void {
  const csv = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(",")).join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

function formatDate(date: string | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

export default function CdkManagement() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [codes, setCodes] = useState<CdkCode[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "unused" | "used">("all");

  const [quickCount12h, setQuickCount12h] = useState(10);
  const [quickCountPermanent, setQuickCountPermanent] = useState(10);
  const [quickDescription, setQuickDescription] = useState("");
  const [exportAfterGenerate, setExportAfterGenerate] = useState(true);

  const [generating12h, setGenerating12h] = useState(false);
  const [generatingPermanent, setGeneratingPermanent] = useState(false);

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [lastGenerated, setLastGenerated] = useState<CdkCode[]>([]);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      let query = supabase.from("cdk_codes").select("*").order("created_at", { ascending: false });

      if (filter === "unused") query = query.eq("is_used", false);
      if (filter === "used") query = query.eq("is_used", true);

      const { data, error } = await query;
      if (error) throw error;

      setCodes((data || []) as CdkCode[]);
    } catch (error) {
      console.error("Failed to fetch cdk codes", error);
      toast({ title: "获取卡密失败", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, [filter]);

  const exportCodes = (list: CdkCode[], filenamePrefix: string) => {
    const headers = [
      "code",
      "description",
      "card_type",
      "grant_premium_hours",
      "expires_at",
      "is_used",
      "used_by",
      "used_at",
      "premium_until",
      "created_at",
    ];

    const rows = list.map((code) => [
      code.code,
      code.description || "",
      code.grant_premium_hours === 12 ? "12H" : "PERMANENT",
      code.grant_premium_hours ?? "",
      code.expires_at ?? "",
      code.is_used ? "true" : "false",
      code.used_by ?? "",
      code.used_at ?? "",
      code.premium_until ?? "",
      code.created_at,
    ]);

    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadCsv(`${filenamePrefix}-${ts}.csv`, headers, rows);
  };

  const createCodes = async (count: number, premiumHours: number | null, typeLabel: "12H" | "PERMANENT") => {
    if (!user?.id) {
      toast({ title: "请先登录管理员" });
      return;
    }

    const safeCount = Math.max(1, Math.min(500, Number(count || 1)));

    const payload = Array.from({ length: safeCount }).map(() => ({
      code: generateRandomCode(),
      description: quickDescription.trim() || null,
      grant_premium_hours: premiumHours,
      created_by: user.id,
      expires_at: null,
    }));

    const { data, error } = await supabase.from("cdk_codes").insert(payload).select("*");
    if (error) throw error;

    const generated = (data || []) as CdkCode[];
    setLastGenerated(generated);

    if (exportAfterGenerate && generated.length > 0) {
      exportCodes(generated, `cdk-${typeLabel.toLowerCase()}`);
    }

    toast({
      title: `已生成 ${generated.length} 张${typeLabel === "12H" ? "12H" : "永久"}卡`,
    });

    await fetchCodes();
  };

  const handleGenerate12h = async () => {
    setGenerating12h(true);
    try {
      await createCodes(quickCount12h, 12, "12H");
    } catch (error) {
      console.error("Failed to generate 12h codes", error);
      toast({ title: "12H 卡生成失败", variant: "destructive" });
    } finally {
      setGenerating12h(false);
    }
  };

  const handleGeneratePermanent = async () => {
    setGeneratingPermanent(true);
    try {
      await createCodes(quickCountPermanent, null, "PERMANENT");
    } catch (error) {
      console.error("Failed to generate permanent codes", error);
      toast({ title: "永久卡生成失败", variant: "destructive" });
    } finally {
      setGeneratingPermanent(false);
    }
  };

  const handleCopyCode = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1200);
    toast({ title: "已复制卡密" });
  };

  const handleInvalidateCode = async (id: string) => {
    try {
      const { error } = await supabase
        .from("cdk_codes")
        .update({ expires_at: new Date().toISOString() })
        .eq("id", id);

      if (error) throw error;

      toast({ title: "卡密已作废" });
      fetchCodes();
    } catch (error) {
      console.error("Failed to invalidate code", error);
      toast({ title: "作废失败", variant: "destructive" });
    }
  };

  const handleDeleteCode = async (id: string) => {
    try {
      const { error } = await supabase.from("cdk_codes").delete().eq("id", id);
      if (error) throw error;

      toast({ title: "卡密已删除" });
      fetchCodes();
    } catch (error) {
      console.error("Failed to delete code", error);
      toast({ title: "删除失败", variant: "destructive" });
    }
  };

  const filteredCodes = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();
    if (!keyword) return codes;

    return codes.filter((code) => {
      return (
        code.code.toLowerCase().includes(keyword) ||
        (code.description || "").toLowerCase().includes(keyword)
      );
    });
  }, [codes, searchQuery]);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">卡密分发</h1>
        <p className="text-muted-foreground">一键生成 12H 卡和永久卡，支持导出 CSV 分发</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>一键生成</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2 rounded-lg border p-4">
              <div className="flex items-center gap-2 font-medium">
                <Clock3 className="h-4 w-4 text-primary" />
                12H 卡
              </div>
              <Label>数量</Label>
              <Input
                type="number"
                min={1}
                max={500}
                value={quickCount12h}
                onChange={(event) => setQuickCount12h(Number(event.target.value || 1))}
              />
              <Button className="w-full" onClick={handleGenerate12h} disabled={generating12h}>
                {generating12h ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
                一键生成 12H
              </Button>
            </div>

            <div className="space-y-2 rounded-lg border p-4">
              <div className="flex items-center gap-2 font-medium">
                <Infinity className="h-4 w-4 text-primary" />
                永久卡
              </div>
              <Label>数量</Label>
              <Input
                type="number"
                min={1}
                max={500}
                value={quickCountPermanent}
                onChange={(event) => setQuickCountPermanent(Number(event.target.value || 1))}
              />
              <Button className="w-full" onClick={handleGeneratePermanent} disabled={generatingPermanent}>
                {generatingPermanent ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
                一键生成 永久
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto]">
            <div className="space-y-2">
              <Label>备注（可选）</Label>
              <Input
                value={quickDescription}
                placeholder="例如：3月活动批次"
                onChange={(event) => setQuickDescription(event.target.value)}
              />
            </div>

            <div className="flex items-end gap-2 rounded-lg border px-3 py-2">
              <div className="text-right">
                <p className="text-sm font-medium">生成后导出 CSV</p>
                <p className="text-xs text-muted-foreground">方便直接发给代理/客服</p>
              </div>
              <Switch checked={exportAfterGenerate} onCheckedChange={setExportAfterGenerate} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>卡密列表</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-10"
                placeholder="搜索卡密或备注"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>

            <Select value={filter} onValueChange={(value: "all" | "unused" | "used") => setFilter(value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="unused">未使用</SelectItem>
                <SelectItem value="used">已使用</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="icon" onClick={fetchCodes}>
              <RefreshCw className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              className="gap-2"
              disabled={filteredCodes.length === 0}
              onClick={() => exportCodes(filteredCodes, "cdk-all")}
            >
              <Download className="h-4 w-4" />
              导出当前筛选
            </Button>

            <Button
              variant="outline"
              className="gap-2"
              disabled={lastGenerated.length === 0}
              onClick={() => exportCodes(lastGenerated, "cdk-last-generated")}
            >
              <Download className="h-4 w-4" />
              导出最近生成
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>卡密</TableHead>
                  <TableHead>卡类型</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>备注</TableHead>
                  <TableHead>创建时间</TableHead>
                  <TableHead>过期时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCodes.length === 0 ? (
                  <TableRow>
                    <TableCell className="py-8 text-center text-muted-foreground" colSpan={7}>
                      暂无数据
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCodes.map((code) => {
                    const cardType = code.grant_premium_hours === 12 ? "12H" : "永久";
                    const expired = isExpired(code.expires_at);

                    return (
                      <TableRow key={code.id}>
                        <TableCell className="font-mono text-sm">{code.code}</TableCell>
                        <TableCell>
                          <Badge
                            className={cn(
                              "border-0",
                              code.grant_premium_hours === 12
                                ? "bg-sky-500/15 text-sky-700"
                                : "bg-emerald-500/15 text-emerald-700",
                            )}
                          >
                            {cardType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {code.is_used ? (
                            <Badge variant="secondary">已使用</Badge>
                          ) : expired ? (
                            <Badge variant="destructive">已过期</Badge>
                          ) : (
                            <Badge className="bg-emerald-500/15 text-emerald-700 border-0">可用</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{code.description || "-"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(code.created_at)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {code.expires_at ? formatDate(code.expires_at) : "永久"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleCopyCode(code.code, code.id)}
                            >
                              {copiedId === code.id ? (
                                <Check className="h-4 w-4 text-emerald-600" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>

                            {!code.is_used && !expired ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-amber-600"
                                onClick={() => handleInvalidateCode(code.id)}
                              >
                                <Ban className="h-4 w-4" />
                              </Button>
                            ) : null}

                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleDeleteCode(code.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
